/** In-memory queue. A failed send is put back once, then dropped. */

import { TOMIS_API_URL } from "@/constants";

import type { StoredSession } from "./session";

export interface QueuedEvent {
  id: string;
  type: string;
  at: string;
  path: string;
  /** Session this event was queued under. A later session must not claim it. */
  session: StoredSession;
  payload?: Record<string, unknown>;
  target_type?: string;
  target_id?: number;
  attempts: number;
}

const MAX_BATCH = 50;
const FLUSH_AT = 20;
/** Newest events are kept. Older ones are dropped if a flush keeps failing. */
const MAX_QUEUED = 200;

let queue: QueuedEvent[] = [];
let sending = false;
/** pagehide arrived while a fetch was in flight. The next send uses sendBeacon. */
let beaconFollowUp = false;

export interface FlushResult {
  trackingDisabled: boolean;
  /** The batch was put back. Do not send it again in this turn. */
  failed: boolean;
  /** Another session's events are still queued. */
  remaining: boolean;
  /** A flush is already running. The caller must not start another. */
  busy: boolean;
  /** Send whatever is left with sendBeacon. */
  followUp: boolean;
}

export function discardQueue(): void {
  queue = [];
}

export function queuedEventTypes(): string[] {
  return queue.map((event) => event.type);
}

export function enqueue(event: QueuedEvent): void {
  queue.push(event);
  if (queue.length > MAX_QUEUED) {
    queue.splice(0, queue.length - MAX_QUEUED);
  }
}

export function shouldFlushNow(): boolean {
  return queue.length >= FLUSH_AT;
}

export function eventsEndpoint(siteSlug: string): string {
  const base = (TOMIS_API_URL || "").replace(/\/$/, "");
  return `${base}/${siteSlug}/web/events/`;
}

export function batchBody(
  visitorId: string,
  session: StoredSession,
  events: QueuedEvent[],
): string {
  return JSON.stringify({
    v: 1,
    visitor_id: visitorId,
    session: {
      id: session.id,
      started_at: new Date(session.startedAt).toISOString(),
      landing_host: session.landingHost,
      landing_path: session.landingPath,
      referrer_host: session.referrerHost,
      utm_source: session.utmSource,
      utm_medium: session.utmMedium,
      utm_campaign: session.utmCampaign,
      click_id_type: session.clickIdType,
      click_id: session.clickId,
    },
    events: events.map((event) => {
      const row: Record<string, unknown> = {
        id: event.id,
        type: event.type,
        at: event.at,
        path: event.path,
      };
      if (event.payload && Object.keys(event.payload).length > 0) {
        row.payload = event.payload;
      }
      if (event.target_type && event.target_id) {
        row.target_type = event.target_type;
        row.target_id = event.target_id;
      }
      return row;
    }),
  });
}

/**
 * Send the queued events. `pagehide` uses sendBeacon; everything else uses a
 * keepalive fetch. Returns whether the tracker should stop (`tracking_enabled: false`).
 */
export async function flushQueue(options: {
  siteSlug: string;
  visitorId: string | null;
  beacon: boolean;
}): Promise<FlushResult> {
  const idle: FlushResult = {
    trackingDisabled: false,
    failed: false,
    remaining: false,
    busy: false,
    followUp: false,
  };
  // takeBatch already removed the in-flight events, so an empty queue here
  // does not mean there is nothing to send. pagehide still arms sendBeacon
  // for the batch if that fetch fails.
  if (sending) {
    if (options.beacon) beaconFollowUp = true;
    return { ...idle, busy: true, followUp: true };
  }
  if (queue.length === 0 || !options.visitorId) {
    beaconFollowUp = false;
    return idle;
  }
  sending = true;
  const url = eventsEndpoint(options.siteSlug);
  let batch: QueuedEvent[] | null = null;
  try {
    if (options.beacon) {
      beaconFollowUp = false;
      // pagehide does not wait for a later turn, so every queued session goes out now.
      while (queue.length > 0) {
        batch = takeBatch();
        const sent = sendBeacon(
          url,
          batchBody(options.visitorId, batch[0].session, batch),
        );
        if (!sent) {
          requeue(batch);
          return done(true);
        }
      }
      return done(false);
    }
    batch = takeBatch();
    const body = batchBody(options.visitorId, batch[0].session, batch);
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body,
      keepalive: true,
    });
    if (!response.ok) {
      requeue(batch);
      return done(true);
    }
    const payload = (await response.json()) as {
      data?: { tracking_enabled?: boolean };
    };
    if (payload.data?.tracking_enabled === false) {
      discardQueue();
      beaconFollowUp = false;
      return {
        trackingDisabled: true,
        failed: false,
        remaining: false,
        busy: false,
        followUp: false,
      };
    }
    return done(false);
  } catch {
    if (batch) requeue(batch);
    return done(true);
  } finally {
    sending = false;
  }
}

/** Events queued under one session, in order, up to the batch cap. */
function takeBatch(): QueuedEvent[] {
  const sessionId = queue[0].session.id;
  const batch: QueuedEvent[] = [];
  const rest: QueuedEvent[] = [];
  for (const event of queue) {
    if (
      rest.length === 0 &&
      batch.length < MAX_BATCH &&
      event.session.id === sessionId
    ) {
      batch.push(event);
    } else {
      rest.push(event);
    }
  }
  queue = rest;
  return batch;
}

function done(failed: boolean): FlushResult {
  return {
    trackingDisabled: false,
    failed,
    remaining: queue.length > 0,
    busy: false,
    followUp: beaconFollowUp,
  };
}

function sendBeacon(url: string, body: string): boolean {
  if (typeof navigator.sendBeacon !== "function") return false;
  try {
    return navigator.sendBeacon(url, new Blob([body], { type: "text/plain" }));
  } catch {
    return false;
  }
}

function requeue(batch: QueuedEvent[]): void {
  const retry: QueuedEvent[] = [];
  for (const event of batch) {
    if (event.attempts >= 1) continue;
    retry.push({ ...event, attempts: event.attempts + 1 });
  }
  queue = retry.concat(queue);
  if (queue.length > MAX_QUEUED) {
    queue.splice(0, queue.length - MAX_QUEUED);
  }
}
